import { ContractError, NetworkError, ApiError } from '@lb/domain'

/**
 * Error reporting and usage measurement, behind one seam with no vendor.
 *
 * ── Why a seam and not a provider ─────────────────────────────────────────
 * Which service this reports to is the operator's decision and has not been
 * taken. Wiring one in now would mean either guessing, or shipping a dependency
 * that has to be torn out. What the app needs is not Sentry specifically — it
 * is *one place* that decides what is reportable and what is never sent.
 *
 * So this is the whole contract. Attaching a vendor later is
 * `setTelemetrySink(...)` in `main.tsx` and nothing else moves.
 *
 * ── The reason error reporting matters here in particular ─────────────────
 * This codebase parses every response with Zod at the transport boundary
 * (§4.6). That discipline is worth much less if a `ContractError` in production
 * is a `console.error` nobody reads: the whole point is to find out the moment
 * the API's shape and ours disagree, not to fail quietly in front of a member.
 *
 * ── What is never sent, and why the redaction is here ────────────────────
 * A parenting app's error payloads are unusually sensitive: a URL can name a
 * question a parent asked about their child, a signed media URL is a **bearer
 * capability** that expires (§4.8), and a request body can contain a post they
 * were writing. Redaction lives at the seam rather than at each call site,
 * because a call site that forgets is the one that leaks.
 */

export interface TelemetryEvent {
  name: string
  /** Only values safe to send. Nothing here is redacted for you downstream. */
  props?: Record<string, string | number | boolean>
}

export interface TelemetrySink {
  reportError: (error: unknown, context: Record<string, unknown>) => void
  trackEvent: (event: TelemetryEvent) => void
}

/**
 * The default sink: development-visible, production-silent.
 *
 * Deliberately not "console.error in production too". A production console
 * nobody watches is the illusion of monitoring, and it is what this is meant to
 * replace rather than imitate.
 */
const consoleSink: TelemetrySink = {
  reportError: (error, context) => {
    if (import.meta.env.DEV) console.error('[lb] error', error, context)
  },
  trackEvent: (event) => {
    if (import.meta.env.DEV) console.info('[lb] event', event.name, event.props ?? {})
  },
}

let sink: TelemetrySink = consoleSink

/** Attach a vendor. Called once, from `main.tsx`, when one is chosen. */
export function setTelemetrySink(next: TelemetrySink) {
  sink = next
}

/**
 * Analytics is gated; error reporting is not, and the distinction is legal
 * rather than a preference.
 *
 * Law 25 requires consent for measurement. It does not require consent to know
 * that the product broke — an error report is part of operating the service the
 * member asked for, provided it carries nothing about them. That proviso is
 * what `redact` below is for, and it is why the gate is expressed as a mutable
 * flag rather than left to each caller to remember.
 */
let analyticsAllowed = false

export function setAnalyticsAllowed(allowed: boolean) {
  analyticsAllowed = allowed
}

/**
 * A URL with its path and query stripped.
 *
 * `/qanda/{id}` names a question a parent asked about their own child;
 * `?q=` on the search route is what they typed while distressed. Neither
 * belongs in a third-party dashboard, and the origin alone is enough to tell
 * which environment failed.
 */
function safeUrl(value: string): string {
  try {
    return new URL(value).origin
  } catch {
    return '[unparseable]'
  }
}

/**
 * What a reported error is allowed to say.
 *
 * Built by allow-list rather than by removing known-bad keys: a deny-list is
 * one new field away from being wrong, and the new field is always the one
 * nobody thought about.
 */
function issuePaths(issues: unknown): string[] {
  if (!Array.isArray(issues)) return []
  return issues
    .map((issue) => {
      if (typeof issue !== 'object' || issue === null || !('path' in issue)) return null
      const path = (issue as { path: unknown }).path
      return Array.isArray(path) ? path.join('.') : null
    })
    .filter((value): value is string => value !== null)
}

function redact(error: unknown): Record<string, unknown> {
  if (error instanceof ContractError) {
    return {
      kind: 'ContractError',
      // The API route whose shape disagreed. Already a template like
      // `/community/rooms/:id/posts`, so it names no member's data.
      path: error.path,
      // The zod issue PATHS only — never the values. Which field disagreed is
      // the entire diagnostic; the value that disagreed is a member's data.
      // `issues` is typed `unknown`, so it is walked defensively rather than
      // cast: a redaction step that throws is a redaction step that gets
      // wrapped in a try/catch and then quietly disabled.
      fields: issuePaths(error.issues),
    }
  }

  if (error instanceof ApiError) {
    // `code` and `status` are the API's own vocabulary. `details` can carry a
    // validation echo of what was submitted, so it is not forwarded.
    return { kind: 'ApiError', status: error.status, code: error.code }
  }

  if (error instanceof NetworkError) {
    return { kind: 'NetworkError' }
  }

  if (error instanceof Error) {
    return { kind: error.name, message: error.message.slice(0, 200) }
  }

  return { kind: 'unknown' }
}

export function reportError(error: unknown, context: Record<string, string | number> = {}) {
  sink.reportError(redact(error), { ...context, origin: safeUrl(window.location.href) })
}

/** A no-op unless the member said yes. `unknown` counts as no. */
export function trackEvent(event: TelemetryEvent) {
  if (!analyticsAllowed) return
  sink.trackEvent(event)
}
